import React, { useMemo, useState } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ChevronLeft, ChevronRight, Check, Pencil, Trash2 } from "lucide-react";
import { TID } from "@/lib/testIds";
import { cn } from "@/lib/utils";
import {
  formatDueDate,
  parseDueDate,
  STATUS_COLOR,
  daysRemainingLabel,
} from "@/lib/status";
import StatusBadge from "@/components/StatusBadge";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function CalendarView({
  rows,
  onEdit,
  onDelete,
  onToggleComplete,
}) {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(() => new Date());

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = useMemo(
    () => eachDayOfInterval({ start: gridStart, end: gridEnd }),
    [gridStart, gridEnd]
  );

  const tasksByDay = useMemo(() => {
    const map = new Map();
    rows.forEach((t) => {
      const key = t.dueDate;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    return map;
  }, [rows]);

  const selectedIso = format(selected, "yyyy-MM-dd");
  const selectedTasks = tasksByDay.get(selectedIso) || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
      <div className="ab-card ab-shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0]">
          <div
            className="text-sm font-semibold text-[#0F172A]"
            data-testid={TID.calendarLabel}
          >
            {format(cursor, "MMMM yyyy")}
          </div>
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCursor(addMonths(cursor, -1))}
              className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:bg-[#F8FAFC]"
              data-testid={TID.calendarPrev}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                setCursor(startOfMonth(now));
                setSelected(now);
              }}
              className="h-7 px-2 inline-flex items-center rounded-md border border-[#E2E8F0] text-xs text-[#334155] hover:bg-[#F8FAFC]"
              data-testid={TID.calendarToday}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setCursor(addMonths(cursor, 1))}
              className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:bg-[#F8FAFC]"
              data-testid={TID.calendarNext}
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-[#E2E8F0] bg-white">
          {DAY_LABELS.map((d) => (
            <div
              key={d}
              className="px-2 py-2 text-[10px] uppercase tracking-wider text-[#64748B] text-center font-medium"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 auto-rows-[minmax(96px,auto)]">
          {days.map((day) => {
            const iso = format(day, "yyyy-MM-dd");
            const inMonth = isSameMonth(day, monthStart);
            const isSelected = isSameDay(day, selected);
            const today = isToday(day);
            const dayTasks = tasksByDay.get(iso) || [];
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setSelected(day)}
                data-testid={TID.calendarDay(iso)}
                className={cn(
                  "text-left border-r border-b border-[#E2E8F0] p-1.5 focus:outline-none focus:z-10 focus:ring-2 focus:ring-[#0F172A]/10",
                  inMonth ? "bg-white" : "bg-[#F8F9FA]",
                  isSelected && "ring-2 ring-[#0F172A]/70 z-10",
                  "hover:bg-[#F8FAFC]"
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      "text-[11px] font-medium tabular-nums",
                      inMonth ? "text-[#0F172A]" : "text-[#94A3B8]",
                      today &&
                        "bg-[#0F172A] text-white rounded-full h-5 w-5 inline-flex items-center justify-center"
                    )}
                  >
                    {format(day, "d")}
                  </span>
                  {dayTasks.length > 0 && (
                    <span className="text-[10px] text-[#64748B] tabular-nums">
                      {dayTasks.length}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-col gap-0.5">
                  {dayTasks.slice(0, 2).map((t) => {
                    const c = STATUS_COLOR[t.derivedStatus] || STATUS_COLOR.Pending;
                    return (
                      <div
                        key={t.id}
                        className="text-[10px] px-1.5 py-0.5 rounded truncate font-medium"
                        style={{ background: c.bg, color: c.fg }}
                        title={t.title}
                      >
                        {t.title}
                      </div>
                    );
                  })}
                  {dayTasks.length > 2 && (
                    <div className="text-[10px] text-[#64748B] px-1.5">
                      +{dayTasks.length - 2} more
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <aside
        className="ab-card ab-shadow-sm p-4 h-fit sticky top-24"
        data-testid={TID.calendarSidebar}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-medium">
              Selected
            </div>
            <div className="text-sm font-semibold text-[#0F172A]">
              {format(selected, "EEEE, dd MMM yyyy")}
            </div>
          </div>
          <span className="text-xs text-[#64748B] tabular-nums">
            {selectedTasks.length} task{selectedTasks.length === 1 ? "" : "s"}
          </span>
        </div>
        {selectedTasks.length === 0 ? (
          <p className="text-xs text-[#64748B]">
            No compliances due on this date.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {selectedTasks.map((task) => (
              <li
                key={task.id}
                className="border border-[#E2E8F0] rounded-md p-2.5"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium text-[#0F172A] leading-snug">
                    {task.title}
                  </div>
                  <StatusBadge status={task.derivedStatus} showDot={false} />
                </div>
                <div className="mt-1 text-[11px] text-[#64748B] flex items-center gap-2">
                  <span className="bg-[#F1F5F9] rounded px-1.5 py-0.5 text-[#334155] font-medium">
                    {task.category}
                  </span>
                  <span>{daysRemainingLabel(task)}</span>
                </div>
                <div className="mt-2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onToggleComplete(task)}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-md px-2 h-6 text-[11px] font-medium border",
                      task.status === "Completed"
                        ? "bg-white border-[#E2E8F0] text-[#64748B]"
                        : "bg-[#0F172A] border-[#0F172A] text-white"
                    )}
                    data-testid={TID.taskToggle(task.id)}
                  >
                    <Check className="h-3 w-3" />
                    {task.status === "Completed" ? "Undo" : "Done"}
                  </button>
                  <button
                    type="button"
                    onClick={() => onEdit(task)}
                    className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155]"
                    data-testid={TID.taskEdit(task.id)}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(task)}
                    className="h-6 w-6 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155]"
                    data-testid={TID.taskDelete(task.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
}
